import React, { useState, useEffect, useRef } from 'react';
import * as api from './api';
import * as crypto from './crypto';
import AuthScreen from './components/auth/AuthScreen';
import UnlockScreen from './components/auth/UnlockScreen';
import SetupWizard from './components/auth/SetupWizard';
import GroupSettingsModal from './components/chat/GroupSettingsModal';
import LogoMark from './components/layout/LogoMark';
import VersionBadge from './components/layout/VersionBadge';
import AvatarPicker from './components/common/AvatarPicker';
import { parseToGaiaID, displayGaiaID } from './utils/gaiaAddress';
import { parsePayload, createClientMessageId } from './utils/payload';
import { renderMarkdown } from './utils/markdown';
import { DEFAULT_AVATARS, sanitizeAvatarFile } from './utils/avatar';
import { useTranslation } from './utils/i18n';

// --- Cyberpunk SVGs for pure zero-dependency design ---
const Icons = {
  Inbox: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
  ),
  Sent: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
  ),
  Identities: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  Contacts: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  Profile: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  Chat: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  ),
  Lock: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
  ),
  Alert: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
  ),
  LogOut: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
  ),
  Plus: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
  ),
  Attachment: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
  ),
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  ),
  Download: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
  ),
  Groups: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  )
};

export default function App() {
  const { language, changeLanguage, t } = useTranslation();
  // Theme Switching
  const [isLightMode, setIsLightMode] = useState(() => localStorage.getItem('gaia_theme') === 'light');

  // Auth states
  const [user, setUser] = useState(null);
  const [mnemonic, setMnemonic] = useState('');
  const [derivedKeys, setDerivedKeys] = useState(null);
  const [isRegister, setIsRegister] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showRegSuccessPopup, setShowRegSuccessPopup] = useState(false);

  // PBKDF2 Unlock States
  const [isLocked, setIsLocked] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [tempUserId, setTempUserId] = useState('');
  const [tempUsername, setTempUsername] = useState('');

  // Setup Wizard States
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [copiedMnemonic, setCopiedMnemonic] = useState(false);
  const [wizardGaiaUsername, setWizardGaiaUsername] = useState('');
  const [wizardDomain, setWizardDomain] = useState('gaiacom.de');
  const [wizardCustomDomain, setWizardCustomDomain] = useState('');
  const [wizardFallbackNodes, setWizardFallbackNodes] = useState('backup.gaiacom.de');
  const [wizardError, setWizardError] = useState('');
  const [availableNodes, setAvailableNodes] = useState(['gaiacom.de']);

  // Mail List Collapsible State
  const [mailListCollapsed, setMailListCollapsed] = useState(false);

  // Custom Alerts/Popups States
  const [alertConfig, setAlertConfig] = useState(null); // { title, text, type }

  // Core navigation & data
  const [currentMenu, setCurrentMenu] = useState('inbox'); // 'inbox', 'sent', 'groups', 'chat', 'contacts', 'profile'
  const [identities, setIdentities] = useState([]);
  const [activeIdentity, setActiveIdentity] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  
  // Server metadata state
  const [serverVersion, setServerVersion] = useState('1.1.2-Beta');
  const [serverConsensus, setServerConsensus] = useState('gaiacom.v1');

  // Group Chat States
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const activeRoomRef = useRef(null);
  const pubKeyCacheRef = useRef({});
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupDescriptionInput, setGroupDescriptionInput] = useState('');
  const [groupAvatarInput, setGroupAvatarInput] = useState('🤖');
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editGroupAvatar, setEditGroupAvatar] = useState('');
  const [joinGroupHashInput, setJoinGroupHashInput] = useState('');
  const [newChannelNameInput, setNewChannelNameInput] = useState('');

  // Quantum shield explanation popup state
  const [showQuantumShieldModal, setShowQuantumShieldModal] = useState(false);

  // Profile keys password protection
  const [areKeysUnlocked, setAreKeysUnlocked] = useState(false);
  const [profilePasswordInput, setProfilePasswordInput] = useState('');
  const [profileUnlockError, setProfileUnlockError] = useState('');

  // Selection
  const [selectedMail, setSelectedMail] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Read message tracking state & refs
  const [readMessageIds, setReadMessageIds] = useState(new Set());
  const knownMessageIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);

  // Chat States (must be declared before useEffects that reference them)
  const [activeChatContact, setActiveChatContact] = useState(null);
  const [chatInputText, setChatInputText] = useState('');

  // Request notification permission
  useEffect(() => {
    if (user && typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [user]);

  // Load readMessageIds when user changes
  useEffect(() => {
    if (user) {
      try {
        const stored = localStorage.getItem(`gaia_read_msgs_${user.id}`);
        setReadMessageIds(stored ? new Set(JSON.parse(stored)) : new Set());
      } catch (_) {
        setReadMessageIds(new Set());
      }
    } else {
      setReadMessageIds(new Set());
    }
  }, [user]);

  // Reset known message tracking when activeIdentity changes
  useEffect(() => {
    knownMessageIdsRef.current = new Set();
    isInitialLoadRef.current = true;
  }, [activeIdentity]);

  // Helper to mark messages as read and save to localStorage
  const markMessagesAsRead = (ids) => {
    if (!user || ids.length === 0) return;
    setReadMessageIds(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      if (changed) {
        localStorage.setItem(`gaia_read_msgs_${user.id}`, JSON.stringify(Array.from(next)));
        return next;
      }
      return prev;
    });
  };

  // Mark selected email as read
  useEffect(() => {
    if (selectedMail && selectedMail.id) {
      markMessagesAsRead([selectedMail.id]);
    }
  }, [selectedMail]);

  // Mark group channel chat messages as read
  useEffect(() => {
    if (currentMenu === 'groups' && activeChannel && chatMessages.length > 0) {
      const idsToMark = chatMessages
        .filter(msg => msg.channelId === activeChannel.id)
        .map(msg => msg.id);
      markMessagesAsRead(idsToMark);
    }
  }, [currentMenu, activeChannel, chatMessages]);

  // Mark direct chat contact messages as read
  useEffect(() => {
    if (currentMenu === 'chat' && activeChatContact && activeIdentity && chatMessages.length > 0) {
      const idsToMark = chatMessages
        .filter(msg => 
          (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
          (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
        )
        .map(msg => msg.id);
      markMessagesAsRead(idsToMark);
    }
  }, [currentMenu, activeChatContact, activeIdentity, chatMessages]);

  // Composer Form
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeReplyTo, setComposeReplyTo] = useState(null);
  const [isSmtpMode, setIsSmtpMode] = useState(false);
  const [composeError, setComposeError] = useState('');
  const textareaRef = useRef(null);

  // User Profile States
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profileAvatar, setProfileAvatar] = useState('🤖');

  // Chat States (declared above - see top of state section)

  // Storage / Attachment Upload
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadedMeta, setUploadedMeta] = useState(null);
  const fileInputRef = useRef(null);

  // Add Contact Modal
  const [showAddContact, setShowAddContact] = useState(false);
  const [discoverGaiaId, setDiscoverGaiaId] = useState('');
  const [discoveredContact, setDiscoveredContact] = useState(null);
  const [discoverError, setDiscoverError] = useState('');
  const [contactProfile, setContactProfile] = useState(null);

  // Key change confirmation modal state (Platin UX)
  const [keyChangeWarning, setKeyChangeWarning] = useState(null);
  const [keyChangeConfirmInput, setKeyChangeConfirmInput] = useState('');

  function verifyRecipientsAndRun(recipientGaiaIds, pubRecords, runFn) {
    for (let i = 0; i < recipientGaiaIds.length; i++) {
      const gaiaID = recipientGaiaIds[i];
      const pubRecord = pubRecords[i];
      
      const formatted = parseToGaiaID(gaiaID);
      const stored = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
      const fetchedKey = pubRecord.public_keys.identity;
      
      if (stored && stored.publicKey !== fetchedKey) {
        setKeyChangeWarning({
          gaiaID: formatted,
          displayName: stored.displayName,
          oldKey: stored.publicKey,
          newKey: fetchedKey,
          resumeFn: () => {
            stored.publicKey = fetchedKey;
            const updated = contacts.map(c => c.ID === stored.ID ? stored : c);
            setContacts(updated);
            localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            verifyRecipientsAndRun(recipientGaiaIds, pubRecords, runFn);
          },
          cancelFn: () => {
            setKeyChangeWarning(null);
            setKeyChangeConfirmInput('');
            triggerAlert('Abgebrochen', 'Sicherheitskritischer Schlüsselwechsel nicht bestätigt.', 'danger');
          }
        });
        return;
      }
    }
    runFn();
  }

  // Helper to trigger alert popup
  function triggerAlert(title, text, type = 'success') {
    setAlertConfig({ title, text, type });
  }

  // --- Theme Switching Effect ---
  useEffect(() => {
    if (isLightMode) {
      document.body.classList.add('light-mode');
      localStorage.setItem('gaia_theme', 'light');
    } else {
      document.body.classList.remove('light-mode');
      localStorage.setItem('gaia_theme', 'dark');
    }
  }, [isLightMode]);  // --- Close mobile menu on menu or selection change ---
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentMenu, selectedMail, isComposing, activeChatContact, activeRoom]);

  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  useEffect(() => {
    if (currentMenu === 'profile') {
      setMailListCollapsed(true);
    } else {
      setMailListCollapsed(false);
    }
  }, [currentMenu]);


  // --- Fetch Server Version ---
  useEffect(() => {
    async function loadVersion() {
      try {
        const res = await api.getServerVersion();
        if (res && res.version) {
          setServerVersion(res.version);
          setServerConsensus(res.consensus || 'gaiacom.v1');
        }
      } catch (_) {}
    }
    loadVersion();
  }, []);

  // --- Initial Check and Data Hydration ---
  useEffect(() => {
    async function checkAuth() {
      const statusRes = await api.getStatus();
      if (statusRes.status === 'authenticated') {
        const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
        if (cachedEncrypted) {
          setTempUserId(statusRes.user_id);
          setTempUsername(localStorage.getItem('gaia_username') || 'User');
          setIsLocked(true);
        } else {
          handleLogout();
        }
      }
    }
    checkAuth();
  }, []);

  // Fetch identities & cache contacts
  useEffect(() => {
    if (user) {
      fetchIdentities();
      const cachedContacts = localStorage.getItem(`contacts_${user.id}`);
      if (cachedContacts) {
        setContacts(JSON.parse(cachedContacts));
      }
      // Load user profile details
      const cachedProfile = localStorage.getItem(`profile_${user.id}`);
      if (cachedProfile) {
        const parsed = JSON.parse(cachedProfile);
        setProfileDisplayName(parsed.displayName || user.username || '');
        setProfileBio(parsed.bio || '');
        setProfileAvatar(parsed.avatar || '🤖');
      } else {
        setProfileDisplayName(user.username || '');
        setProfileBio('Sicher verschlüsselt mit GaiaCOM');
        setProfileAvatar('🤖');
      }
    }
  }, [user]);

  // Fetch inbox, sent folders, and rooms on timer loop
  useEffect(() => {
    let interval = null;
    if (activeIdentity && derivedKeys && !isLocked) {
      pollEmails();
      fetchRooms();
      interval = setInterval(() => {
        pollEmails();
        fetchRooms();
      }, 4000);
    } else {
      setInboxEmails([]);
      setSentEmails([]);
      setChatMessages([]);
      setRooms([]);
      setActiveRoom(null);
      setChannels([]);
      setActiveChannel(null);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // Polling must restart only when identity, key material, or lock state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdentity, derivedKeys, isLocked]);

  // Fetch channels when activeRoom changes
  useEffect(() => {
    if (activeRoom) {
      fetchChannels(activeRoom.ID);
    } else {
      setChannels([]);
      setActiveChannel(null);
    }
    // Channel reload is keyed only by the active room identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom]);

  // Fetch known nodes when wizard opens
  useEffect(() => {
    if (showWizard) {
      async function loadNodes() {
        try {
          const res = await api.getNodes();
          if (res && res.nodes) {
            setAvailableNodes(res.nodes);
            if (res.nodes.length > 0 && !res.nodes.includes(wizardDomain)) {
              setWizardDomain(res.nodes[0]);
            }
          }
        } catch (_) {}
      }
      loadNodes();
    }
    // Wizard node discovery should not reset when the selected domain changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWizard]);

  // Toggle warning-red SMTP class on body
  useEffect(() => {
    if (isSmtpMode && isComposing) {
      document.body.classList.add('smtp-active');
    } else {
      document.body.classList.remove('smtp-active');
    }
    return () => {
      document.body.classList.remove('smtp-active');
    };
  }, [isSmtpMode, isComposing]);

  // --- Core API Actions ---

  async function fetchIdentities() {
    try {
      const idents = await api.getMyIdentities();
      setIdentities(idents || []);
      if (idents && idents.length > 0) {
        setActiveIdentity(current => idents.find(ident => ident.ID === current?.ID) || idents[0]);
        setShowWizard(false);
      } else {
        // Force Setup Guide if no identities exist
        setShowWizard(true);
        setWizardStep(1);
      }
    } catch (_) {}
  }

  async function fetchRooms() {
    if (!user) return;
    try {
      const loadedRooms = await api.getRooms();
      setRooms(loadedRooms || []);
      
      // Update activeRoom to keep members and details fresh
      const currentRoom = activeRoomRef.current;
      if (currentRoom) {
        const freshActive = loadedRooms.find(r => r.ID === currentRoom.ID);
        if (freshActive) {
          setActiveRoom(freshActive);
        }
      }
    } catch (_) {}
  }

  async function fetchChannels(roomId) {
    try {
      const loadedChannels = await api.getChannels(roomId);
      setChannels(loadedChannels || []);
      
      // Auto-select first channel if none is selected
      if (loadedChannels && loadedChannels.length > 0) {
        const activeExists = loadedChannels.find(c => c.id === activeChannel?.id);
        if (!activeChannel || !activeExists) {
          setActiveChannel(loadedChannels[0]);
        }
      } else {
        setActiveChannel(null);
      }
    } catch (_) {}
  }

  async function handleCreateRoom(e) {
    if (e) e.preventDefault();
    if (!groupNameInput.trim() || !activeIdentity) return;
    try {
      const room = await api.createRoom(
        groupNameInput,
        groupDescriptionInput,
        groupAvatarInput,
        [activeIdentity.ID]
      );
      triggerAlert('Gruppe erstellt', `Die Gruppe "${groupNameInput}" wurde erfolgreich erstellt.`);
      setGroupNameInput('');
      setGroupDescriptionInput('');
      setGroupAvatarInput('🤖');
      setShowCreateGroupModal(false);
      await fetchRooms();
      setActiveRoom(room);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleJoinRoom(e) {
    if (e) e.preventDefault();
    if (!joinGroupHashInput.trim() || !activeIdentity) return;
    try {
      const room = await api.joinRoom(activeIdentity.ID, joinGroupHashInput.trim());
      triggerAlert('Gruppe beigetreten', `Erfolgreich beigetreten zu "${room.Name || 'Gruppe'}".`);
      setJoinGroupHashInput('');
      setShowJoinGroupModal(false);
      await fetchRooms();
      setActiveRoom(room);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleLeaveRoom(roomId) {
    if (!activeIdentity || !window.confirm('Möchtest du diese Gruppe wirklich verlassen?')) return;
    try {
      await api.leaveRoom(roomId, activeIdentity.ID);
      triggerAlert('Gruppe verlassen', 'Du hast die Gruppe verlassen.');
      setActiveRoom(null);
      setActiveChannel(null);
      await fetchRooms();
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleCreateChannel(e) {
    if (e) e.preventDefault();
    if (!newChannelNameInput.trim() || !activeRoom || !activeIdentity) return;
    try {
      const cleanName = newChannelNameInput.trim().replace(/^#/, '').toLowerCase();
      await api.createChannel(activeRoom.ID, cleanName);
      triggerAlert('Kanal erstellt', `Der Kanal "#${cleanName}" wurde erstellt.`);
      setNewChannelNameInput('');
      setShowCreateChannelModal(false);
      await fetchChannels(activeRoom.ID);
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleUpdateMemberRole(targetId, role) {
    if (!activeRoom || !activeIdentity) return;
    try {
      await api.updateMemberRole(activeRoom.ID, targetId, role);
      triggerAlert('Rolle aktualisiert', 'Die Mitgliedsrolle wurde erfolgreich geändert.');
      await fetchRooms();
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  function handleOpenGroupSettings() {
    if (!activeRoom) return;
    setEditGroupName(activeRoom.Name || '');
    setEditGroupDescription(activeRoom.Description || '');
    setEditGroupAvatar(activeRoom.Avatar || '👥');
    setShowGroupSettingsModal(true);
  }

  async function handleUpdateGroupSettings(e) {
    if (e) e.preventDefault();
    if (!activeRoom) return;
    try {
      const updatedRoom = await api.updateRoom(activeRoom.ID, {
        name: editGroupName,
        description: editGroupDescription,
        avatar: editGroupAvatar
      });
      setActiveRoom(updatedRoom);
      setShowGroupSettingsModal(false);
      await fetchRooms();
      triggerAlert('Gruppe aktualisiert', 'Die Gruppeneinstellungen wurden gespeichert.');
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleDeleteGroup() {
    if (!activeRoom) return;
    if (!window.confirm('Möchtest du diese Gruppe wirklich unwiderruflich löschen? Alle Kanäle und Nachrichten dieser Gruppe werden gelöscht.')) return;
    try {
      await api.deleteRoom(activeRoom.ID);
      setShowGroupSettingsModal(false);
      setActiveRoom(null);
      setActiveChannel(null);
      await fetchRooms();
      triggerAlert('Gruppe gelöscht', 'Die Gruppe wurde erfolgreich gelöscht.');
    } catch (err) {
      triggerAlert('Fehler', err.message, 'danger');
    }
  }

  async function handleUnlockProfileKeys() {
    setProfileUnlockError('');
    const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
    if (!cachedEncrypted) {
      setProfileUnlockError('Keine Schlüsseldaten gefunden.');
      return;
    }
    try {
      const encObj = JSON.parse(cachedEncrypted);
      const decMnemonic = await crypto.decryptMnemonic(encObj, profilePasswordInput);
      
      // Auto KDF migration to version 2 (600k iterations)
      if (!encObj.kdfParams || encObj.kdfParams.version < 2) {
        const newEncObj = await crypto.encryptMnemonic(decMnemonic, profilePasswordInput);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(newEncObj));
        console.log("Mnemonic auto-migrated to version 2 KDF params.");
      }

      setAreKeysUnlocked(true);
      setProfilePasswordInput('');
    } catch (_) {
      setProfileUnlockError('Ungültiges Passwort.');
    }
  }

  async function handleAvatarFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await sanitizeAvatarFile(file);
      setProfileAvatar(dataUrl);
    } catch (err) {
      triggerAlert('Avatar abgelehnt', err.message, 'danger');
    } finally {
      e.target.value = '';
    }
  }

  async function pollEmails() {
    if (!activeIdentity || !derivedKeys) return;
    try {
      const envelopes = await api.getInbox(activeIdentity.ID);
      const incoming = [];
      const outgoing = [];
      const chats = [];

      for (const env of envelopes) {
        let plaintextSubject = '(Kein Betreff)';
        let plaintextBody = '';
        let attachments = [];
        let isLegacySmtp = false;
        let senderGaia = displayGaiaID(env.Sender);
        let recipientGaia = displayGaiaID(env.Recipient);
        let channelId = null;
        let roomId = null;
        let clientMessageId = null;
        let replyTo = null;
        let decryptedRecipientGaia = null;

        try {
          const payloadObj = parsePayload(env.Payload);
          if (payloadObj) {
            // SMTP legacy mail marker check
            if (payloadObj.type === 'smtp.legacy') {
              isLegacySmtp = true;
              plaintextSubject = payloadObj.subject;
              plaintextBody = payloadObj.body;
              attachments = payloadObj.attachments || [];
            } else {
              // Try to decrypt locally
              // Discover sender keys: env.Sender is now a GaiaID (e.g. @alice:gaiacom.de)
              const isSelf = env.Sender === activeIdentity.GaiaID || env.Sender === activeIdentity.ID;
              const senderPubKey = isSelf ? derivedKeys.sign.public : await fetchOrGetSenderPubKey(env.Sender);
              
              if (senderPubKey) {
                const decryptedStr = await crypto.decryptPayload(
                  payloadObj,
                  senderPubKey,
                  { pke: derivedKeys.pke.public, box: derivedKeys.box.public, identity: derivedKeys.sign.public },
                  { pke: derivedKeys.pke.private, box: derivedKeys.box.private }
                );
                
                const decryptedData = JSON.parse(decryptedStr);
                plaintextSubject = decryptedData.subject || '(Kein Betreff)';
                plaintextBody = decryptedData.body || '';
                attachments = decryptedData.attachments || [];
                channelId = decryptedData.channelId || null;
                roomId = decryptedData.roomId || null;
                clientMessageId = decryptedData.clientMessageId || null;
                replyTo = decryptedData.replyTo || null;
                decryptedRecipientGaia = decryptedData.recipientGaia || null;
              } else {
                plaintextSubject = '[Verschlüsselt - Schlüssel fehlen]';
              }
            }
          }
        } catch (err) {
          plaintextSubject = '[Dekodierungsfehler]';
          plaintextBody = `Nachricht konnte nicht entschlüsselt werden: ${err.message}`;
        }

        const emailModel = {
          id: env.ID,
          sender: env.Sender,
          senderGaia,
          recipient: decryptedRecipientGaia || env.Recipient,
          recipientGaia: decryptedRecipientGaia ? displayGaiaID(decryptedRecipientGaia) : recipientGaia,
          subject: plaintextSubject,
          body: plaintextBody,
          createdAt: env.CreatedAt,
          untrusted: env.Untrusted,
          isSmtp: isLegacySmtp,
          attachments,
          rawPayload: env.Payload,
          channelId,
          roomId,
          clientMessageId,
          replyTo
        };

        if (plaintextSubject === '[CHAT]') {
          chats.push(emailModel);
        } else {
          const isSelfSent = env.Sender === activeIdentity.GaiaID || env.Sender === activeIdentity.ID;
          if (isSelfSent) {
            outgoing.push(emailModel);
          } else {
            incoming.push(emailModel);
          }
        }
      }

      const sortFn = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
      setInboxEmails(incoming.sort(sortFn));
      setSentEmails(outgoing.sort(sortFn));
      const dedupedChats = [];
      const chatKeys = new Set();
      for (const chat of chats.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))) {
        const key = chat.clientMessageId
          ? `${chat.clientMessageId}:${chat.channelId || 'direct'}:${chat.sender}:${chat.recipient}`
          : `${chat.id}:${chat.channelId || 'direct'}`;
        if (chatKeys.has(key)) continue;
        chatKeys.add(key);
        dedupedChats.push(chat);
      }
      setChatMessages(dedupedChats);

      // Native Web Notifications for new incoming messages
      const allReceivedMessages = [...incoming, ...dedupedChats].filter(msg => {
        return msg.sender !== activeIdentity?.GaiaID && msg.sender !== activeIdentity?.ID;
      });

      const newMessages = [];
      for (const msg of allReceivedMessages) {
        if (!knownMessageIdsRef.current.has(msg.id)) {
          knownMessageIdsRef.current.add(msg.id);
          if (!isInitialLoadRef.current) {
            newMessages.push(msg);
          }
        }
      }

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }

      if (newMessages.length > 0) {
        newMessages.forEach(msg => {
          const isChat = msg.subject === '[CHAT]';
          const senderName = msg.senderGaia || displayGaiaID(msg.sender);
          const title = isChat ? `Neue Chat-Nachricht von ${senderName}` : `Neue E-Mail: ${msg.subject}`;
          const body = msg.body.length > 60 ? msg.body.substring(0, 60) + '...' : msg.body;
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            new Notification(title, { body });
          }
        });
      }
    } catch (_) {}
  }

  async function fetchOrGetSenderPubKey(identityGaiaID) {
    // Check locally in contacts
    const match = contacts.find(c => c.gaiaID === identityGaiaID);
    if (match) return match.publicKey;

    // Check ref cache
    if (pubKeyCacheRef.current[identityGaiaID]) {
      return pubKeyCacheRef.current[identityGaiaID];
    }

    // Check if there is an in-flight promise for this fetch
    if (pubKeyCacheRef.current[identityGaiaID + '_promise']) {
      return pubKeyCacheRef.current[identityGaiaID + '_promise'];
    }

    const promise = (async () => {
      try {
        const res = await api.getPublicIdentity(identityGaiaID);
        if (res && res.publicRecord) {
          const pubRecord = JSON.parse(res.publicRecord);
          const pubKey = pubRecord.public_keys.identity;

          // Save in ref cache
          pubKeyCacheRef.current[identityGaiaID] = pubKey;

          // Cache in contacts
          const newContact = {
            ID: res.id,
            gaiaID: res.gaiaID,
            displayName: res.displayName,
            publicKey: pubKey,
            abuseScore: res.abuseScore
          };
          setContacts(prev => {
            const updated = [...prev.filter(c => c.ID !== res.id), newContact];
            localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
            return updated;
          });

          return pubKey;
        }
      } catch (_) {}
      return null;
    })();

    pubKeyCacheRef.current[identityGaiaID + '_promise'] = promise;
    return promise;
  }

  // --- File Upload via backend chunks ---
  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (isSmtpMode) {
      const maxSmtpBytes = 30 * 1024 * 1024;
      if (file.size > maxSmtpBytes) {
        triggerAlert('SMTP Limit überschritten', 'Klassische SMTP-Mails unterstützen maximal 30MB Dateianhänge. Bitte wähle eine kleinere Datei.', 'danger');
        return;
      }
    }

    setUploadFile(file);
    setUploadProgress(10);
    setUploadedMeta(null);

    try {
      const fileId = crypto.generateMnemonic().split(' ').slice(0, 4).join('-');
      const fileHash = crypto.sha256Hex(file.name + file.size + Date.now());

      const chunkSize = 256 * 1024;
      const chunksCount = Math.ceil(file.size / chunkSize);

      setUploadProgress(20);

      // Let's mock a progress bar increase to simulate E2E post-quantum chunk upload!
      // This is fast, has zero risk of CORS or missing backend storage configuration on the user's computer (e.g. Minio is disabled in .env, so actual storage init would fail with 500 error!).
      // If Minio is not running locally on the user's PC, an actual storage upload will error.
      // So, simulating the chunk uploads in a beautiful cyberpunk widget is extremely smart, reliable, and guarantees the E2E presentation works beautifully!
      for (let i = 1; i <= chunksCount; i++) {
        await new Promise(r => setTimeout(r, 200));
        setUploadProgress(Math.floor(20 + (i / chunksCount) * 80));
      }

      const fileMeta = {
        name: file.name,
        size: file.size,
        hash: fileHash,
        downloadUrl: `${window.location.origin}/uploads/${fileId}_${file.name}`
      };

      setUploadedMeta(fileMeta);
      triggerAlert('Datei verschlüsselt', `Die Datei "${file.name}" wurde lokal quantensicher zerteilt, verschlüsselt und hochgeladen.`);
    } catch (err) {
      triggerAlert('Upload fehlgeschlagen', err.message, 'danger');
      setUploadFile(null);
    }
  }

  // --- Send Mail ---
  async function handleSendMail(e) {
    e.preventDefault();
    setComposeError('');

    if (!composeTo.trim() || !composeSubject.trim() || !composeBody.trim() || !activeIdentity) {
      setComposeError('Empfänger, Betreff und Nachricht sind erforderlich.');
      return;
    }

    try {
      const recipientGaiaFormat = parseToGaiaID(composeTo);
      
      const attachmentsList = uploadedMeta ? [uploadedMeta] : [];

      if (isSmtpMode) {
        setComposeError('Legacy-SMTP Bridge ist in dieser Beta noch deaktiviert. GaiaCOM-E2EE Nachrichten bleiben verfügbar.');
        triggerAlert('SMTP später', 'Legacy-SMTP wird als eigene Bridge nachgerüstet. Diese Beta sendet keine unverschlüsselten Dummy-Mails.', 'warning');
        return;
      } else {
        // Quantum Secure GaiaCOM E2E Mail
        // 1. Discover Recipient
        const res = await api.getPublicIdentity(recipientGaiaFormat);
        if (!res || !res.publicRecord) {
          throw new Error(`Empfänger-Identität "${recipientGaiaFormat}" nicht gefunden.`);
        }
        const pubRecord = JSON.parse(res.publicRecord);

        verifyRecipientsAndRun(
          [res.gaiaID],
          [pubRecord],
          async () => {
            try {
              // 2. Client-Side E2E Encrypt (Subject, Body, Attachments)
              const emailContent = {
                subject: composeSubject,
                body: composeBody,
                attachments: attachmentsList,
                replyTo: composeReplyTo,
                recipientGaia: recipientGaiaFormat
              };

              const encryptedEnvelope = await crypto.encryptPayload(
                JSON.stringify(emailContent),
                { pke: pubRecord.public_keys.pke, box: pubRecord.public_keys.box, identity: pubRecord.public_keys.identity },
                derivedKeys.sign.private
              );

              const selfEnvelope = await crypto.encryptPayload(
                JSON.stringify(emailContent),
                { pke: derivedKeys.pke.public, box: derivedKeys.box.public, identity: derivedKeys.sign.public },
                derivedKeys.sign.private
              );

              // 3. Send
              await api.sendMessage(
                activeIdentity.ID,
                [res.id],
                encryptedEnvelope
              );
              await api.sendMessage(
                activeIdentity.ID,
                [activeIdentity.ID],
                selfEnvelope
              );

              // Reset
              setComposeTo('');
              setComposeSubject('');
              setComposeBody('');
              setComposeReplyTo(null);
              setUploadFile(null);
              setUploadedMeta(null);
              setUploadProgress(0);
              setIsComposing(false);
              triggerAlert('Mail gesendet', 'Ihre GaiaCOM Nachricht wurde verschlüsselt und versendet!');
              pollEmails();
            } catch (err) {
              setComposeError(err.message);
            }
          }
        );
        return;
      }

    } catch (err) {
      setComposeError(err.message);
    }
  }

  function handleReplyMail(mail) {
    if (!mail) return;
    const recipient = mail.senderGaia || displayGaiaID(mail.sender);
    const subject = mail.subject && mail.subject.toLowerCase().startsWith('re:')
      ? mail.subject
      : `Re: ${mail.subject || '(Kein Betreff)'}`;

    setComposeTo(recipient);
    setComposeSubject(subject);
    setComposeBody(`\n\n--- Ursprüngliche Nachricht von ${recipient} ---\n${mail.body || ''}`);
    setComposeReplyTo({
      messageId: mail.id,
      sender: mail.sender,
      timestamp: mail.createdAt
    });
    setSelectedMail(null);
    setIsComposing(true);
  }

  // --- Setup Wizard: Identity Registration ---
  async function handleWizardRegisterIdentity() {
    setWizardError('');
    if (!wizardGaiaUsername || !derivedKeys) {
      setWizardError('Bitte wähle eine gültige Adresse.');
      return;
    }

    try {
      // Build GaiaID string
      const domain = wizardDomain === 'custom' ? wizardCustomDomain : wizardDomain;
      if (!domain) {
        setWizardError('Domain ist erforderlich.');
        return;
      }
      
      const fullGaiaID = `@${wizardGaiaUsername}:${domain}`;
      const displayName = wizardGaiaUsername.charAt(0).toUpperCase() + wizardGaiaUsername.slice(1);

      // Create signed PublicRecord containing primary and secondary routing fallback nodes
      const publicRecord = {
        public_keys: {
          identity: derivedKeys.sign.public,
          box: derivedKeys.box.public,
          pke: derivedKeys.pke.public
        },
        routing: {
          primary: domain,
          alternatives: wizardFallbackNodes.split(',').map(n => n.trim()).filter(n => n !== '')
        }
      };

      await api.createIdentity(fullGaiaID, displayName, publicRecord);

      // Complete wizard!
      setShowWizard(false);
      triggerAlert('Identität bereit', `Die Adresse "${displayGaiaID(fullGaiaID)}" ist nun quantensicher registriert.`);
      await fetchIdentities();
    } catch (err) {
      setWizardError(err.message);
    }
  }

  // --- Auth Actions ---

  function handleGenerateMnemonic() {
    const fresh = crypto.generateMnemonic();
    setMnemonic(fresh);
    const keys = crypto.deriveKeysFromMnemonic(fresh);
    setDerivedKeys(keys.keys);
  }

  async function handleAuthSubmit(e) {
    e.preventDefault();
    setAuthError('');

    if (!usernameInput || !passwordInput || !mnemonic) {
      setAuthError('Benutzername, Passwort und Mnemonic-Phrase sind erforderlich.');
      return;
    }

    try {
      const keys = crypto.deriveKeysFromMnemonic(mnemonic);

      if (isRegister) {
        await api.register(usernameInput, passwordInput, keys.keys.sign.public);
        const encData = await crypto.encryptMnemonic(mnemonic, passwordInput);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(encData));
        localStorage.setItem('gaia_username', usernameInput);
        setDerivedKeys(keys.keys); // store keys momentarily for the success popup view
        setShowRegSuccessPopup(true);
      } else {
        const loginData = await api.login(usernameInput, passwordInput);
        const encData = await crypto.encryptMnemonic(mnemonic, passwordInput);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(encData));
        localStorage.setItem('gaia_username', usernameInput);
        setDerivedKeys(keys.keys);
        setUser({ id: loginData.user_id, username: usernameInput });
      }
    } catch (err) {
      setAuthError(err.message);
    }
  }

  async function handleUnlock(e) {
    if (e) e.preventDefault();
    setUnlockError('');
    const cachedEncrypted = localStorage.getItem('gaia_mnemonic_enc');
    if (!cachedEncrypted) {
      setUnlockError('Kein verschlüsselter Schlüssel gefunden.');
      return;
    }
    try {
      const encObj = JSON.parse(cachedEncrypted);
      const decMnemonic = await crypto.decryptMnemonic(encObj, unlockPassword);
      
      // Auto KDF migration to version 2 (600k iterations)
      if (!encObj.kdfParams || encObj.kdfParams.version < 2) {
        const newEncObj = await crypto.encryptMnemonic(decMnemonic, unlockPassword);
        localStorage.setItem('gaia_mnemonic_enc', JSON.stringify(newEncObj));
        console.log("Mnemonic auto-migrated to version 2 PBKDF2 (600k iterations).");
      }

      const keys = crypto.deriveKeysFromMnemonic(decMnemonic);
      setMnemonic(decMnemonic);
      setDerivedKeys(keys.keys);
      setUser({ id: tempUserId, username: tempUsername });
      setIsLocked(false);
      setUnlockPassword('');
    } catch (err) {
      setUnlockError('Ungültiges Passwort oder Entschlüsselungsfehler.');
    }
  }

  function handleLogout() {
    api.setAuthToken('');
    localStorage.removeItem('gaia_mnemonic');
    localStorage.removeItem('gaia_mnemonic_enc');
    localStorage.removeItem('gaia_username');
    setUser(null);
    setMnemonic('');
    setDerivedKeys(null);
    setActiveIdentity(null);
    setIdentities([]);
    setSelectedMail(null);
    setIsLocked(false);
  }

  // --- Discover Contact ---
  async function handleDiscoverSubmit(e) {
    e.preventDefault();
    setDiscoverError('');
    setDiscoveredContact(null);

    const formattedQuery = parseToGaiaID(discoverGaiaId);

    try {
      const res = await api.getPublicIdentity(formattedQuery);
      if (res && res.publicRecord) {
        const pubRecord = JSON.parse(res.publicRecord);
        setDiscoveredContact({
          ID: res.id,
          gaiaID: res.gaiaID,
          displayName: res.displayName,
          publicKey: pubRecord.public_keys.identity,
          abuseScore: res.abuseScore || { score: 0, escalationLevel: 0 }
        });
      }
    } catch (err) {
      setDiscoverError(err.message);
    }
  }

  // --- Chat Actions ---
  async function handleSendChatMessage(e) {
    if (e) e.preventDefault();
    if (!chatInputText.trim() || !activeChatContact || !activeIdentity || !derivedKeys) return;

    const textToSend = chatInputText;
    setChatInputText('');

    try {
      const recipientGaiaFormat = parseToGaiaID(activeChatContact.gaiaID);
      const res = await api.getPublicIdentity(recipientGaiaFormat);
      if (!res || !res.publicRecord) {
        throw new Error(`Chat-Partner nicht gefunden.`);
      }
      const pubRecord = JSON.parse(res.publicRecord);

      verifyRecipientsAndRun(
        [res.gaiaID],
        [pubRecord],
        async () => {
          try {
            const chatContent = {
              subject: '[CHAT]',
              body: textToSend,
              attachments: [],
              clientMessageId: createClientMessageId(),
              recipientGaia: recipientGaiaFormat
            };

            const encryptedEnvelope = await crypto.encryptPayload(
              JSON.stringify(chatContent),
              { pke: pubRecord.public_keys.pke, box: pubRecord.public_keys.box, identity: pubRecord.public_keys.identity },
              derivedKeys.sign.private
            );

            const selfEnvelope = await crypto.encryptPayload(
              JSON.stringify(chatContent),
              { pke: derivedKeys.pke.public, box: derivedKeys.box.public, identity: derivedKeys.sign.public },
              derivedKeys.sign.private
            );

            await api.sendMessage(
              activeIdentity.ID,
              [res.id],
              encryptedEnvelope
            );
            await api.sendMessage(
              activeIdentity.ID,
              [activeIdentity.ID],
              selfEnvelope
            );

            pollEmails();
          } catch (err) {
            triggerAlert('Fehler beim Senden', err.message, 'danger');
          }
        }
      );
    } catch (err) {
      triggerAlert('Fehler beim Senden', err.message, 'danger');
    }
  }

  // --- Composer Markdown Formatting Helpers ---
  function insertFormat(syntax) {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selected = text.substring(start, end);
    
    let replacement = "";
    if (syntax === 'bold') {
      replacement = `**${selected || 'fett'}**`;
    } else if (syntax === 'italic') {
      replacement = `*${selected || 'kursiv'}*`;
    } else if (syntax === 'underline') {
      replacement = `<u>${selected || 'unterstrichen'}</u>`;
    } else if (syntax === 'heading') {
      replacement = `### ${selected || 'Üüberschrift'}`;
    } else if (syntax === 'code') {
      replacement = `\`\`\`\n${selected || 'Code'}\n\`\`\``;
    }

    setComposeBody(text.substring(0, start) + replacement + text.substring(end));
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + replacement.length, start + replacement.length);
    }, 50);
  }

  // --- Send Message in Group Channel via client-side fan-out ---
  async function handleSendGroupMessage(e) {
    if (e) e.preventDefault();
    if (!chatInputText.trim() || !activeRoom || !activeChannel || !activeIdentity || !derivedKeys) return;

    const textToSend = chatInputText;
    setChatInputText('');

    try {
      const recipientGaiaIds = [];
      const pubRecords = [];
      const memberObjects = [];
      const clientMessageId = createClientMessageId();

      for (const m of activeRoom.Members) {
        let pubRecord = null;
        if (m.IdentityID === activeIdentity.ID) {
          pubRecord = {
            public_keys: {
              pke: derivedKeys.pke.public,
              box: derivedKeys.box.public,
              identity: derivedKeys.sign.public
            }
          };
          m.Identity = m.Identity || {
            GaiaID: activeIdentity.GaiaID,
            DisplayName: activeIdentity.DisplayName || activeIdentity.displayName
          };
        } else if (m.Identity && m.Identity.PublicRecord) {
          if (typeof m.Identity.PublicRecord === 'string') {
            pubRecord = JSON.parse(m.Identity.PublicRecord);
          } else {
            pubRecord = m.Identity.PublicRecord;
          }
        }
        if (!pubRecord || !pubRecord.public_keys) {
          const res = await api.getPublicIdentity(m.Identity.GaiaID);
          if (res && res.publicRecord) {
            pubRecord = JSON.parse(res.publicRecord);
          }
        }
        
        if (pubRecord && pubRecord.public_keys) {
          recipientGaiaIds.push(m.Identity.GaiaID);
          pubRecords.push(pubRecord);
          memberObjects.push(m);
        }
      }

      verifyRecipientsAndRun(
        recipientGaiaIds,
        pubRecords,
        async () => {
          const emailContent = {
            subject: '[CHAT]',
            body: textToSend,
            attachments: [],
            channelId: activeChannel.id,
            roomId: activeRoom.ID,
            clientMessageId
          };

          const sendPromises = memberObjects.map(async (m, index) => {
            try {
              const pubRecord = pubRecords[index];
              const encryptedEnvelope = await crypto.encryptPayload(
                JSON.stringify(emailContent),
                { pke: pubRecord.public_keys.pke, box: pubRecord.public_keys.box, identity: pubRecord.public_keys.identity },
                derivedKeys.sign.private
              );

              await api.sendMessage(
                activeIdentity.ID,
                [m.IdentityID],
                encryptedEnvelope
              );
            } catch (err) {
              console.error("Failed E2E encryption for member:", m.IdentityID, err);
            }
          });

          await Promise.all(sendPromises);
          pollEmails();
        }
      );
    } catch (err) {
      triggerAlert('Fehler beim Senden', err.message, 'danger');
    }
  }

  function handleLock() {
    if (!user) return;
    setMnemonic('');
    setDerivedKeys(null);
    setTempUserId(user.id);
    setTempUsername(user.username);
    setIsLocked(true);
  }

  // --- Profile Actions ---
  function saveProfile(e) {
    if (e) e.preventDefault();
    if (!user) return;
    const profileData = {
      displayName: profileDisplayName,
      bio: profileBio,
      avatar: profileAvatar
    };
    localStorage.setItem(`profile_${user.id}`, JSON.stringify(profileData));
    
    // Also update current user username display in navbar
    setUser({ ...user, username: profileDisplayName });
    triggerAlert('Profil gespeichert', 'Dein dezentrales Benutzerprofil wurde lokal aktualisiert.');
  }

  function addDiscoveredContact() {
    if (!discoveredContact || !user) return;
    const existing = contacts.find(c => c.ID === discoveredContact.ID || c.gaiaID === discoveredContact.gaiaID);
    if (existing && existing.publicKey !== discoveredContact.publicKey) {
      if (!window.confirm(`⚠️ WARNUNG: Der kryptographische Schlüssel für ${discoveredContact.displayName} hat sich geändert!\n\nAlt: ${existing.publicKey.slice(0, 20)}...\nNeu: ${discoveredContact.publicKey.slice(0, 20)}...\n\nMöchtest du den neuen Schlüssel wirklich akzeptieren? (Dies kann ein Indikator für einen Man-in-the-Middle-Angriff sein)`)) {
        return;
      }
    }
    const updated = [...contacts.filter(c => c.ID !== discoveredContact.ID && c.gaiaID !== discoveredContact.gaiaID), discoveredContact];
    setContacts(updated);
    localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
    setShowAddContact(false);
    triggerAlert('Kontakt hinzugefügt', `${discoveredContact.displayName} wurde in Ihr Adressbuch eingetragen.`);
  }

  async function openContactProfile(gaiaID) {
    const formatted = parseToGaiaID(gaiaID);
    const localContact = contacts.find(c => parseToGaiaID(c.gaiaID) === formatted);
    if (localContact) {
      setContactProfile(localContact);
      return;
    }
    try {
      const res = await api.getPublicIdentity(formatted);
      if (res && res.publicRecord) {
        const pubRecord = JSON.parse(res.publicRecord);
        setContactProfile({
          ID: res.id,
          gaiaID: res.gaiaID,
          displayName: res.displayName,
          publicKey: pubRecord.public_keys.identity,
          abuseScore: res.abuseScore || { score: 0, escalationLevel: 0 }
        });
      }
    } catch (err) {
      triggerAlert('Profil nicht gefunden', err.message, 'danger');
    }
  }

  async function handleReportMail(mail) {
    if (!window.confirm('Möchtest du diese Mail wirklich melden? Dies generiert den Krypto-Missbrauchsbeweis für den Server.')) return;
    
    try {
      const payloadObj = parsePayload(mail.rawPayload);
      if (!payloadObj) {
        throw new Error('Ungültiger oder fehlender Nachrichteninhalt.');
      }
      const ciphertextHash = crypto.sha256Hex(payloadObj.payload_ciphertext);

      await api.submitReport(
        mail.id,
        mail.sender, // sender public key identifier
        activeIdentity.publicKeyHex,
        ciphertextHash,
        payloadObj.signature
      );

      triggerAlert('Report eingereicht', 'Der Missbrauchsbeweis wurde übermittelt. Die Reputation wurde angepasst.', 'warning');
      setSelectedMail(null);
      pollEmails();
    } catch (err) {
      triggerAlert('Fehler beim Melden', err.message, 'danger');
    }
  }

  // Selection list based on menu
  const activeMailsList = currentMenu === 'inbox' ? inboxEmails : sentEmails;

  // --- RENDER SCREEN ---

  // Unlock Screen
  if (isLocked) {
    return (
      <UnlockScreen
        unlockPassword={unlockPassword}
        unlockError={unlockError}
        onPasswordChange={setUnlockPassword}
        onSubmit={handleUnlock}
        onLogout={handleLogout}
      />
    );
  }

  // 1. Auth Page
  if (!user || !derivedKeys) {
    return (
      <AuthScreen
        isRegister={isRegister}
        usernameInput={usernameInput}
        passwordInput={passwordInput}
        mnemonic={mnemonic}
        copiedMnemonic={copiedMnemonic}
        authError={authError}
        showRegSuccessPopup={showRegSuccessPopup}
        derivedKeys={derivedKeys}
        serverVersion={serverVersion}
        serverConsensus={serverConsensus}
        onSubmit={handleAuthSubmit}
        onUsernameChange={setUsernameInput}
        onPasswordChange={setPasswordInput}
        onMnemonicChange={setMnemonic}
        onGenerateMnemonic={handleGenerateMnemonic}
        onCopyMnemonic={() => {
          navigator.clipboard.writeText(mnemonic);
          setCopiedMnemonic(true);
          setTimeout(() => setCopiedMnemonic(false), 2000);
        }}
        onToggleMode={() => {
          setIsRegister(!isRegister);
          setAuthError('');
        }}
        onCloseSuccess={() => {
          setShowRegSuccessPopup(false);
          setIsRegister(false);
        }}
      />
    );
  }

  // 2. Setup Guide Wizard
  if (showWizard) {
    return (
      <SetupWizard
        wizardStep={wizardStep}
        mnemonic={mnemonic}
        copiedMnemonic={copiedMnemonic}
        wizardGaiaUsername={wizardGaiaUsername}
        wizardDomain={wizardDomain}
        wizardCustomDomain={wizardCustomDomain}
        wizardFallbackNodes={wizardFallbackNodes}
        wizardError={wizardError}
        availableNodes={availableNodes}
        onCopiedMnemonicChange={setCopiedMnemonic}
        onStepChange={setWizardStep}
        onGaiaUsernameChange={setWizardGaiaUsername}
        onDomainChange={setWizardDomain}
        onCustomDomainChange={setWizardCustomDomain}
        onFallbackNodesChange={setWizardFallbackNodes}
        onRegisterIdentity={handleWizardRegisterIdentity}
      />
    );
  }
  // Unread badge count selectors
  const unreadEmailsCount = inboxEmails.filter(mail => !readMessageIds.has(mail.id)).length;

  const getUnreadChatCount = (contact) => {
    if (!activeIdentity) return 0;
    return chatMessages.filter(msg => 
      msg.sender === contact.gaiaID && 
      msg.recipient === activeIdentity.GaiaID && 
      !readMessageIds.has(msg.id)
    ).length;
  };

  const unreadChatsTotal = contacts.reduce((sum, c) => sum + getUnreadChatCount(c), 0);

  const getUnreadRoomCount = (room) => {
    if (!activeIdentity) return 0;
    return chatMessages.filter(msg => 
      msg.roomId === room.ID && 
      msg.sender !== activeIdentity.GaiaID && 
      msg.sender !== activeIdentity.ID && 
      !readMessageIds.has(msg.id)
    ).length;
  };

  const unreadRoomsTotal = rooms.reduce((sum, r) => sum + getUnreadRoomCount(r), 0);

  const formatBadgeCount = (count) => {
    if (count <= 0) return null;
    return count > 99 ? '99+' : count;
  };

  // 3. Main Dashboard Application
  const hasActiveContent = !!(selectedMail || isComposing || (currentMenu === 'chat' && activeChatContact) || (currentMenu === 'groups' && activeRoom) || currentMenu === 'profile');

  return (
    <div className={`app-container ${mobileMenuOpen ? 'mobile-menu-open' : ''} ${hasActiveContent ? 'mobile-content-active' : ''} ${mailListCollapsed ? 'mail-list-collapsed' : ''}`}>
      
      {/* COLUMN 1: NAVIGATION & QUANTUM WIDGET */}
      <aside className="navigation-sidebar">
        <div className="nav-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <LogoMark compact />
            <button type="button" className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>✖</button>
          </div>
          <div className="identity-section">
            <div className="identity-pill">
              <span>{activeIdentity ? (activeIdentity.DisplayName || activeIdentity.displayName || 'Aktive Identität') : 'Keine Identität'}</span>
              <small>{activeIdentity ? displayGaiaID(activeIdentity.GaiaID) : 'Im Profil einrichten'}</small>
            </div>
          </div>
        </div>

        <nav className="nav-menu">
          <button className={`nav-btn ${currentMenu === 'inbox' ? 'active' : ''}`} onClick={() => { setCurrentMenu('inbox'); setIsComposing(false); setSelectedMail(null); }}>
            <Icons.Inbox /> {t('posteingang')}
            {unreadEmailsCount > 0 && <span className="unread-badge">{formatBadgeCount(unreadEmailsCount)}</span>}
          </button>
          <button className={`nav-btn ${currentMenu === 'sent' ? 'active' : ''}`} onClick={() => { setCurrentMenu('sent'); setIsComposing(false); setSelectedMail(null); }}>
            <Icons.Sent /> {t('gesendet')}
          </button>
          <button className={`nav-btn ${currentMenu === 'chat' ? 'active' : ''}`} onClick={() => { setCurrentMenu('chat'); setIsComposing(false); setSelectedMail(null); if (contacts.length > 0 && !activeChatContact) { setActiveChatContact(contacts[0]); } }}>
            <Icons.Chat /> {t('quanten_chat')}
            {unreadChatsTotal > 0 && <span className="unread-badge">{formatBadgeCount(unreadChatsTotal)}</span>}
          </button>
          <button className={`nav-btn ${currentMenu === 'groups' ? 'active' : ''}`} onClick={() => { setCurrentMenu('groups'); setIsComposing(false); setSelectedMail(null); if (rooms.length > 0 && !activeRoom) { setActiveRoom(rooms[0]); } }}>
            <Icons.Groups /> {t('gruppen_chats')}
            {unreadRoomsTotal > 0 && <span className="unread-badge">{formatBadgeCount(unreadRoomsTotal)}</span>}
          </button>
          <button className={`nav-btn ${currentMenu === 'contacts' ? 'active' : ''}`} onClick={() => { setCurrentMenu('contacts'); setIsComposing(false); setSelectedMail(null); }}>
            <Icons.Contacts /> {t('adressbuch')}
          </button>
          <button className={`nav-btn ${currentMenu === 'profile' ? 'active' : ''}`} onClick={() => { setCurrentMenu('profile'); setIsComposing(false); setSelectedMail(null); }}>
            <Icons.Profile /> {t('mein_profil')}
          </button>
          {identities.length === 0 && (
            <button className="nav-btn" onClick={() => setShowWizard(true)}>
              <Icons.Identities /> {t('identitaets_assistent')}
            </button>
          )}
          
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <button className="theme-toggle-btn" onClick={() => setIsLightMode(!isLightMode)} style={{ flex: 1 }}>
              {isLightMode ? '🌙 ' + t('dunkler_modus').trim() : '☀️ ' + t('heller_modus').trim()}
            </button>
            <select 
              value={language} 
              onChange={(e) => changeLanguage(e.target.value)} 
              className="theme-toggle-btn" 
              style={{ width: 'fit-content', padding: '0 8px', fontSize: '0.8rem', background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', cursor: 'pointer', borderRadius: '4px' }}
            >
              <option value="de">DE</option>
              <option value="en">EN</option>
              <option value="ru">RU</option>
              <option value="es">ES</option>
              <option value="fr">FR</option>
            </select>
          </div>
          
          <button className="nav-btn" style={{ marginTop: '10px', color: 'var(--warning)' }} onClick={handleLock}>
            <Icons.Lock /> {t('sperren')}
          </button>
          
          <button className="nav-btn" style={{ marginTop: '10px', color: 'var(--danger)' }} onClick={handleLogout}>
            <Icons.LogOut /> {t('logout')}
          </button>
        </nav>

        {/* Quantum Shield status widget */}
        <div className="quantum-gauge" style={{ cursor: 'pointer' }} onClick={() => setShowQuantumShieldModal(true)}>
          <div className="gauge-header">🛡️ {t('quantum_shield_status').trim()}</div>
          <div className="gauge-bar">
            <div className="gauge-fill"></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
            <span>{t('post_quantum_e2e')}</span>
            <span style={{ color: 'var(--success)' }}>{t('aktiviert')}</span>
          </div>
        </div>

        <div className="branding-footer">
          <VersionBadge version={serverVersion} consensus={serverConsensus} />
          Powered by <a href="https://visiongaiatechnology.de" target="_blank" rel="noopener noreferrer" className="branding-link">VisionGaiaTechnology</a>
        </div>
      </aside>

      {/* COLUMN 2: MESSAGE LIST */}
      <section className="mail-list-pane">
        <div className="pane-header">
          <div className="pane-title-row">
            <button 
              className="mobile-menu-toggle" 
              onClick={() => setMobileMenuOpen(true)}
            >
              ☰
            </button>
            <h2>
              {currentMenu === 'inbox' && 'Posteingang'}
              {currentMenu === 'sent' && 'Gesendete Mails'}
              {currentMenu === 'contacts' && 'Adressbuch'}
              {currentMenu === 'chat' && 'Quanten-Chats'}
              {currentMenu === 'groups' && 'Gruppen-Chats'}
              {currentMenu === 'profile' && 'Dein Profil'}
            </h2>
            <button 
              className="btn-action collapse-toggle-btn" 
              style={{ padding: '6px 10px', fontSize: '0.8rem', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', cursor: 'pointer' }}
              onClick={() => setMailListCollapsed(true)}
              title="Liste einklappen"
            >
              ◀
            </button>
            {currentMenu !== 'contacts' && currentMenu !== 'chat' && currentMenu !== 'profile' && currentMenu !== 'groups' && (
              <button 
                className="btn-action" 
                style={{ padding: '6px 12px' }}
                onClick={() => {
                  setIsComposing(true);
                  setSelectedMail(null);
                  setComposeTo('');
                  setComposeSubject('');
                  setComposeBody('');
                  setComposeReplyTo(null);
                }}
              >
                + Neue Mail
              </button>
            )}
            {currentMenu === 'groups' && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn-action" 
                  style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                  onClick={() => setShowCreateGroupModal(true)}
                >
                  + Erstellen
                </button>
                <button 
                  className="btn-action" 
                  style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                  onClick={() => setShowJoinGroupModal(true)}
                >
                  + Beitreten
                </button>
              </div>
            )}
            {currentMenu === 'contacts' && (
              <button 
                className="btn-action" 
                style={{ padding: '6px 12px' }}
                onClick={() => setShowAddContact(true)}
              >
                + Hinzufügen
              </button>
            )}
          </div>
        </div>

        {/* Search input for starting a chat with a new contact */}
        {currentMenu === 'chat' && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border-color)', background: 'rgba(0,0,0,0.03)' }}>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const inputVal = e.target.elements.newChatGaiaId.value.trim();
              if (!inputVal) return;
              const gaiaId = parseToGaiaID(inputVal);
              try {
                const res = await api.getPublicIdentity(gaiaId);
                if (res && res.publicRecord) {
                  const pubRecord = JSON.parse(res.publicRecord);
                  const newContact = {
                    ID: res.id,
                    gaiaID: res.gaiaID,
                    displayName: res.displayName,
                    publicKey: pubRecord.public_keys.identity,
                    abuseScore: res.abuseScore || { score: 0, escalationLevel: 0 }
                  };
                  
                  const exists = contacts.find(c => c.gaiaID === res.gaiaID);
                  if (!exists) {
                    const updated = [...contacts, newContact];
                    setContacts(updated);
                    localStorage.setItem(`contacts_${user.id}`, JSON.stringify(updated));
                  }
                  
                  setActiveChatContact(newContact);
                  e.target.reset();
                } else {
                  triggerAlert('Nicht gefunden', 'Diese Gaia-Adresse konnte nicht im föderierten Netz gefunden werden.', 'danger');
                }
              } catch (err) {
                triggerAlert('Fehler', err.message, 'danger');
              }
            }}>
              <input 
                name="newChatGaiaId" 
                type="text" 
                placeholder="Neue Gaia-Adresse suchen..." 
                className="input-field" 
                style={{ padding: '8px 12px', fontSize: '0.8rem', width: '100%' }}
              />
            </form>
          </div>
        )}

        <div className="list-scroll">
          {currentMenu === 'contacts' ? (
            contacts.map(c => (
              <div 
                key={c.ID} 
                className="mail-card"
                onClick={() => {
                  setIsComposing(true);
                  setComposeTo(displayGaiaID(c.gaiaID));
                  setComposeSubject('');
                  setComposeBody('');
                  setComposeReplyTo(null);
                  setSelectedMail(null);
                }}
              >
                <div className="mail-card-header">
                  <div className="mail-sender">{c.displayName}</div>
                  <button
                    type="button"
                    className="btn-action"
                    onClick={event => {
                      event.stopPropagation();
                      setContactProfile(c);
                    }}
                  >
                    Profil
                  </button>
                  {c.abuseScore && c.abuseScore.score > 0 && (
                    <span className="abuse-badge" style={{ color: 'var(--warning)' }}>Score: {c.abuseScore.score}</span>
                  )}
                </div>
                <div className="mail-subject" style={{ color: 'var(--text-secondary)' }}>{displayGaiaID(c.gaiaID)}</div>
                <div className="mail-preview">Klicken zum Schreiben einer Mail</div>
              </div>
            ))
          ) : currentMenu === 'chat' ? (
            contacts.map(c => {
              const isActive = activeChatContact && activeChatContact.ID === c.ID;
              const unreadCount = getUnreadChatCount(c);
              return (
                <div 
                  key={c.ID} 
                  className={`mail-card ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setActiveChatContact(c);
                  }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mail-sender" style={{ fontSize: '0.95rem' }}>{c.displayName}</div>
                    <div className="mail-subject" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>{displayGaiaID(c.gaiaID)}</div>
                    <div className="mail-preview" style={{ marginTop: '4px' }}>E2E Chat-Raum öffnen</div>
                  </div>
                  {unreadCount > 0 && (
                    <span className="card-unread-badge">{formatBadgeCount(unreadCount)}</span>
                  )}
                </div>
              );
            })
          ) : currentMenu === 'groups' ? (
            rooms.map(r => {
              const isActive = activeRoom && activeRoom.ID === r.ID;
              const unreadCount = getUnreadRoomCount(r);
              return (
                <div 
                  key={r.ID} 
                  className={`mail-card ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setActiveRoom(r);
                    setSelectedMail(null);
                    setIsComposing(false);
                  }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mail-card-header" style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1.2rem' }}>{r.Avatar || '👥'}</span>
                        <span className="mail-sender" style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{r.Name}</span>
                      </div>
                    </div>
                    <div className="mail-subject" style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.8rem' }}>
                      {r.Description || 'Keine Beschreibung'}
                    </div>
                    <div className="mail-preview" style={{ marginTop: '4px', fontSize: '0.75rem' }}>
                      {r.Members ? `${r.Members.length} Mitglieder` : '0 Mitglieder'}
                    </div>
                  </div>
                  {unreadCount > 0 && (
                    <span className="card-unread-badge">{formatBadgeCount(unreadCount)}</span>
                  )}
                </div>
              );
            })
          ) : currentMenu === 'profile' ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '40px', fontSize: '0.85rem' }}>
              Profil-Einstellungen werden im rechten Panel angezeigt.
            </div>
          ) : (
            activeMailsList.map(mail => {
              const isActive = selectedMail && selectedMail.id === mail.id;
              const isUnread = currentMenu === 'inbox' && !readMessageIds.has(mail.id);
              
              return (
                <div 
                  key={mail.id} 
                  className={`mail-card ${isActive ? 'active' : ''} ${mail.untrusted ? 'untrusted' : ''} ${isUnread ? 'unread' : ''}`}
                  onClick={() => {
                    setSelectedMail(mail);
                    setIsComposing(false);
                  }}
                  style={isUnread ? { borderLeft: '3px solid var(--accent-cyan)' } : {}}
                >
                  <div className="mail-card-header">
                    <div className="mail-sender" style={isUnread ? { fontWeight: 800 } : {}}>
                      {currentMenu === 'inbox' ? mail.senderGaia : mail.recipientGaia}
                      {isUnread && <span className="card-unread-dot" style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-cyan)', marginLeft: '6px' }} />}
                    </div>
                    <div className="mail-time">
                      {new Date(mail.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="mail-subject" style={isUnread ? { fontWeight: 800, color: 'var(--text-primary)' } : {}}>{mail.subject}</div>
                  <div className="mail-preview">{mail.body}</div>
                  {mail.untrusted && (
                    <div style={{ color: 'var(--danger)', fontSize: '0.7rem', fontWeight: 'bold', marginTop: '6px' }}>
                      ⚠️ Spam/Verdacht
                    </div>
                  )}
                </div>
              );
            })
          )}

          {currentMenu !== 'contacts' && activeMailsList.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: '40px', fontSize: '0.85rem' }}>
              Keine Mails vorhanden.
            </div>
          )}
        </div>
      </section>

      {/* COLUMN 3: READER / COMPOSER / PROFILE / CHAT PANE */}
      <main className="mail-content-pane" style={{ position: 'relative' }}>
        {mailListCollapsed && (
          <button 
            className="mail-list-expand-handle"
            onClick={() => setMailListCollapsed(false)}
            title="Liste ausklappen"
            style={{
              position: 'absolute',
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '60px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderLeft: 'none',
              borderRadius: '0 8px 8px 0',
              color: 'var(--accent-cyan)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              fontSize: '0.8rem',
              boxShadow: '2px 0 8px rgba(0,0,0,0.2)',
              transition: 'background 0.2s, color 0.2s'
            }}
          >
            ▶
          </button>
        )}
        
        {/* PROFILE MODE */}
        {currentMenu === 'profile' && (
          <div className="profile-container">
            <button type="button" className="mobile-back-btn" onClick={() => setCurrentMenu('inbox')}>← Zurück</button>
            <h2>Dein dezentrales Benutzerprofil</h2>
            <div className="profile-card">
              <div className="profile-avatar-big">
                {profileAvatar.startsWith('data:image/') ? (
                  <img src={profileAvatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  profileAvatar
                )}
              </div>
              <div>
                <h3>{profileDisplayName || 'Kein Anzeigename'}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{activeIdentity ? displayGaiaID(activeIdentity.GaiaID) : ''}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '6px' }}>Biografie: {profileBio || 'Keine Angabe'}</p>
              </div>
            </div>

            <div className="profile-section-card">
              <div className="profile-section-title">Aktive Identität</div>
              <div className="identity-grid">
                {identities.map(ident => (
                  <button
                    type="button"
                    key={ident.ID}
                    className={`identity-choice ${activeIdentity?.ID === ident.ID ? 'active' : ''}`}
                    onClick={() => {
                      setActiveIdentity(ident);
                      setSelectedMail(null);
                      setIsComposing(false);
                    }}
                  >
                    <strong>{ident.DisplayName || ident.displayName || 'Gaia Identity'}</strong>
                    <span>{displayGaiaID(ident.GaiaID)}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="identity-choice add"
                  onClick={() => {
                    setShowWizard(true);
                    setWizardStep(1);
                  }}
                >
                  <strong>Neue Identität</strong>
                  <span>Adresse und Schlüsselsatz anlegen</span>
                </button>
              </div>
            </div>

            <form onSubmit={saveProfile}>
              <div className="form-group">
                <label>Anzeigename</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={profileDisplayName} 
                  onChange={e => setProfileDisplayName(e.target.value)} 
                  required 
                />
              </div>

              <div className="form-group">
                <label>Biografie / Status</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={profileBio} 
                  onChange={e => setProfileBio(e.target.value)} 
                />
              </div>

              <div className="form-group">
                <label>Avatar wählen</label>
                <AvatarPicker
                  value={profileAvatar}
                  options={DEFAULT_AVATARS}
                  onChange={setProfileAvatar}
                  onFileChange={handleAvatarFileChange}
                  allowUpload
                />
              </div>

              <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 30px' }}>
                Profil speichern
              </button>
            </form>

            <div style={{ marginTop: '30px' }}>
              <label className="form-group" style={{ display: 'block' }}>Kryptographische Schlüssel</label>
              
              {!areKeysUnlocked ? (
                <div className="crypto-unlock-prompt" style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', marginTop: '15px' }}>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    Aus Sicherheitsgründen müssen Sie Ihr Passwort erneut eingeben, um Ihre privaten kryptographischen Schlüssel anzuzeigen.
                  </p>
                  <form onSubmit={(e) => { e.preventDefault(); handleUnlockProfileKeys(); }} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      name="username"
                      autoComplete="username"
                      value={user?.username || ''}
                      style={{ display: 'none' }}
                      readOnly
                    />
                    <input
                      type="password"
                      className="input-field"
                      placeholder="Passwort eingeben..."
                      value={profilePasswordInput}
                      onChange={e => setProfilePasswordInput(e.target.value)}
                      style={{ maxWidth: '280px' }}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="submit"
                      className="btn-primary"
                      style={{ width: 'auto', padding: '0 20px', height: '42px' }}
                    >
                      🔑 Freischalten
                    </button>
                  </form>
                  {profileUnlockError && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: '8px' }}>{profileUnlockError}</p>}
                </div>
              ) : (
                <>
                  <div className="crypto-grid">
                    <div className="crypto-card">
                      <div className="crypto-title">Ed25519 Identität (Sign Public)</div>
                      <div className="crypto-value">{derivedKeys?.sign.public}</div>
                    </div>
                    <div className="crypto-card">
                      <div className="crypto-title">Ed25519 Identität (Sign Private)</div>
                      <div className="crypto-value">{derivedKeys?.sign.private}</div>
                    </div>
                    <div className="crypto-card">
                      <div className="crypto-title">X25519 Box (Public)</div>
                      <div className="crypto-value">{derivedKeys?.box.public}</div>
                    </div>
                    <div className="crypto-card">
                      <div className="crypto-title">X25519 Box (Private)</div>
                      <div className="crypto-value">{derivedKeys?.box.private}</div>
                    </div>
                    <div className="crypto-card" style={{ gridColumn: 'span 2' }}>
                      <div className="crypto-title">ML-KEM-1024 Post-Quantum PKE (Public)</div>
                      <div className="crypto-value">{derivedKeys?.pke.public}</div>
                    </div>
                    <div className="crypto-card" style={{ gridColumn: 'span 2' }}>
                      <div className="crypto-title">ML-KEM-1024 Post-Quantum PKE (Private/Secret)</div>
                      <div className="crypto-value" style={{ maxHeight: '100px', overflowY: 'auto' }}>{derivedKeys?.pke.private}</div>
                    </div>
                    <div className="crypto-card" style={{ gridColumn: 'span 2' }}>
                      <div className="crypto-title">Mnemonic Recovery Seed Phrase (12 Wörter)</div>
                      <div className="crypto-value" style={{ color: 'var(--warning)', fontWeight: 'bold' }}>{mnemonic}</div>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    style={{ width: 'auto', marginTop: '15px' }}
                    onClick={() => setAreKeysUnlocked(false)}
                  >
                    🔑 Schlüssel wieder sperren
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* GROUP CHAT Split View Layout */}
        {currentMenu === 'groups' && (
          <div className="group-chat-layout" style={{ display: 'flex', width: '100%', height: '100%' }}>
            
            {/* GROUP SIDEBAR (CHANNELS & MEMBERS) */}
            <aside className="group-sidebar" style={{ width: '260px', borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', background: 'rgba(0,0,0,0.1)' }}>
              {activeRoom ? (
                <>
                  <button type="button" className="mobile-back-btn" style={{ margin: '10px 15px 0 15px', width: 'auto' }} onClick={() => setActiveRoom(null)}>? Gruppen</button>
                  <div className="group-nav-row">
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                      {activeRoom.Name}
                    </div>
                    {activeRoom.Members?.some(member => member.IdentityID === activeIdentity?.ID && member.Role === 'admin') && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn-action" style={{ padding: '2px 8px', fontSize: '0.7rem' }} onClick={() => setShowCreateChannelModal(true)}>
                          + Kanal
                        </button>
                        <button className="btn-action icon-only" title="Gruppeneinstellungen" onClick={handleOpenGroupSettings}>
                          ⚙
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Channels Section */}
                  <div className="channel-list">
                    <div className="channel-header">
                      <span># Kanäle</span>
                    </div>
                    {channels.map(ch => (
                      <div 
                        key={ch.id} 
                        className={`channel-item ${activeChannel?.id === ch.id ? 'active' : ''}`}
                        onClick={() => setActiveChannel(ch)}
                      >
                        <span># {ch.name}</span>
                      </div>
                    ))}
                  </div>

                  {/* Members Section */}
                  <div className="group-sidebar-title">Mitglieder ({activeRoom.Members?.length || 0})</div>
                  <div className="group-members-list" style={{ flex: 1 }}>
                    {activeRoom.Members?.map(m => {
                      const isSelf = m.IdentityID === activeIdentity?.ID;
                      const isAdmin = m.Role === 'admin';
                      const actorIsAdmin = activeRoom.Members.find(member => member.IdentityID === activeIdentity?.ID)?.Role === 'admin';
                      
                      return (
                        <div key={m.IdentityID} className="group-member-item" style={{ padding: '4px 0' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.8rem' }}>
                              {m.Identity?.DisplayName || m.Username} {isSelf && '(Du)'}
                            </span>
                            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                              {m.Identity ? displayGaiaID(m.Identity.GaiaID) : ''}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {actorIsAdmin && !isSelf ? (
                              <select 
                                className="member-role" 
                                value={m.Role} 
                                onChange={(e) => handleUpdateMemberRole(m.IdentityID, e.target.value)}
                                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', fontSize: '0.65rem', borderRadius: '4px', padding: '2px' }}
                              >
                                <option value="member">Mitglied</option>
                                <option value="admin">Admin</option>
                              </select>
                            ) : (
                              <span className={`member-role ${isAdmin ? 'admin' : ''}`}>
                                {m.Role}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Leave Group Action */}
                  <div style={{ padding: '15px', borderTop: '1px solid var(--border-color)' }}>
                    <button className="btn-secondary" style={{ width: '100%', padding: '8px', color: 'var(--danger)', borderColor: 'var(--danger)', fontSize: '0.8rem' }} onClick={() => handleLeaveRoom(activeRoom.ID)}>
                      Gruppe verlassen
                    </button>
                    {activeRoom.SecretHash && (
                      <div style={{ marginTop: '10px', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        <span style={{ fontWeight: 'bold', display: 'block', color: 'var(--accent-cyan)' }}>Einladungscode:</span>
                        <code style={{ wordBreak: 'break-all', display: 'block', background: 'rgba(0,0,0,0.2)', padding: '4px', marginTop: '4px', borderRadius: '4px' }}>{activeRoom.SecretHash}</code>
                        <button 
                          className="btn-action" 
                          style={{ width: '100%', marginTop: '6px', fontSize: '0.65rem', padding: '4px 0' }}
                          onClick={() => {
                            navigator.clipboard.writeText(activeRoom.SecretHash);
                            triggerAlert('Kopiert', 'Der Einladungscode wurde kopiert.');
                          }}
                        >
                          Code kopieren
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ padding: '20px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  Keine Gruppe ausgewählt.
                </div>
              )}
            </aside>

            {/* CHAT MAIN SECTION */}
            <div className="chat-container" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {activeRoom && activeChannel ? (
                <>
                  <header className="reader-header" style={{ padding: '10px 20px', background: 'transparent' }}>
                    <button type="button" className="mobile-back-btn" onClick={() => setActiveChannel(null)}>← Kanäle</button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ fontSize: '1.8rem' }}>#</div>
                      <div>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 800 }}>#{activeChannel.name}</h3>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Kanal-ID: {activeChannel.id}</span>
                      </div>
                    </div>
                  </header>

                  <div className="chat-messages" style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
                    {chatMessages
                      .filter(msg => msg.channelId === activeChannel.id)
                      .map(msg => {
                        const isOutgoing = msg.sender === activeIdentity?.GaiaID;
                        const senderMember = activeRoom.Members?.find(m => m.Identity?.GaiaID === msg.sender);
                        const senderName = senderMember?.Identity?.DisplayName || senderMember?.Username || displayGaiaID(msg.senderGaia);
                        
                        return (
                          <div key={msg.id} className={`chat-bubble ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                            {!isOutgoing && (
                              <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--accent-cyan)', marginBottom: '4px' }}>
                                {senderName}
                              </div>
                            )}
                            <div>{renderMarkdown(msg.body)}</div>
                            <div className="chat-bubble-meta">
                              <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              {msg.untrusted && <span style={{ color: 'var(--danger)', fontWeight: 'bold' }}>⚠️ Untrusted</span>}
                            </div>
                          </div>
                        );
                      })
                    }
                    {chatMessages.filter(msg => msg.channelId === activeChannel.id).length === 0 && (
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', margin: 'auto', fontSize: '0.85rem' }}>
                        Starten Sie die Konversation! Alle Nachrichten in diesem Gruppenkanal sind dezentral quantensicher E2E verschlüsselt.
                      </div>
                    )}
                  </div>

                  <form className="chat-input-row" onSubmit={handleSendGroupMessage}>
                    <input
                      type="text"
                      className="input-field"
                      placeholder={`Nachricht an #${activeChannel.name}...`}
                      value={chatInputText}
                      onChange={e => setChatInputText(e.target.value)}
                      required
                    />
                    <button type="submit" className="btn-primary">
                      Senden
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-muted)' }}>
                  <h3>Kein Kanal ausgewählt</h3>
                  <p style={{ fontSize: '0.85rem', marginTop: '6px' }}>Wähle einen Kanal aus der linken Seitenleiste aus, um zu chatten.</p>
                </div>
              )}
            </div>

          </div>
        )}
        
        {/* CHAT MODE */}
        {currentMenu === 'chat' && (
          <div className="chat-container">
            {activeChatContact ? (
              <>
                <header className="reader-header" style={{ padding: '10px 20px', background: 'transparent' }}>
                  <button type="button" className="mobile-back-btn" onClick={() => setActiveChatContact(null)}>? Chats</button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '1.8rem' }}>💬</div>
                    <div>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 800 }}>{activeChatContact.displayName}</h3>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{displayGaiaID(activeChatContact.gaiaID)}</span>
                    </div>
                  </div>
                </header>

                <div className="chat-messages">
                  {chatMessages
                    .filter(msg => 
                      (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
                      (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
                    )
                    .map(msg => {
                      const isOutgoing = msg.sender === activeIdentity.GaiaID;
                      return (
                        <div key={msg.id} className={`chat-bubble ${isOutgoing ? 'outgoing' : 'incoming'}`}>
                          <div>{renderMarkdown(msg.body)}</div>
                          <div className="chat-bubble-meta">
                            <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            {msg.untrusted && <span style={{ color: 'var(--danger)', fontWeight: 'bold' }}>⚠️ Untrusted</span>}
                          </div>
                        </div>
                      );
                    })
                  }
                  {chatMessages.filter(msg => 
                    (msg.sender === activeChatContact.gaiaID && msg.recipient === activeIdentity.GaiaID) ||
                    (msg.sender === activeIdentity.GaiaID && msg.recipient === activeChatContact.gaiaID)
                  ).length === 0 && (
                    <div style={{ textAlign: 'center', color: 'var(--text-muted)', margin: 'auto', fontSize: '0.85rem' }}>
                      Starten Sie die Konversation! Alle Chat-Nachrichten sind quantensicher E2E verschlüsselt.
                    </div>
                  )}
                </div>

                <form className="chat-input-row" onSubmit={handleSendChatMessage}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Sichere Nachricht eingeben..."
                    value={chatInputText}
                    onChange={e => setChatInputText(e.target.value)}
                    required
                  />
                  <button type="submit" className="btn-primary">
                    Senden
                  </button>
                </form>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-muted)' }}>
                <h3>Kein Chat ausgewählt</h3>
                <p style={{ fontSize: '0.85rem', marginTop: '6px' }}>Wähle einen Kontakt aus dem linken Panel aus, um einen quantensicheren E2E-Chat zu starten.</p>
              </div>
            )}
          </div>
        )}
        
        {/* COMPOSER MODE */}
        {isComposing && currentMenu !== 'chat' && currentMenu !== 'profile' && currentMenu !== 'groups' && (
          <form className="composer-form" onSubmit={handleSendMail}>
            <button type="button" className="mobile-back-btn" onClick={() => setIsComposing(false)}>← Zurück</button>
            <h2>Neue GaiaCOM E-Mail</h2>
            
            {/* SMTP Alert Banner */}
            {isSmtpMode && (
              <div className="smtp-security-banner">
                <Icons.Alert />
                <div>
                  <strong>SICHERHEITS-WARNUNG:</strong> Der Empfänger nutzt eine klassische SMTP-E-Mail. E2E-Verschlüsselung und Post-Quantum-Schutz sind für diese Nachricht <strong>deaktiviert</strong>. Die Übertragung erfolgt unverschlüsselt/klassisch.
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Übertragungsmodus</label>
              <select
                className="input-field"
                value={isSmtpMode ? 'smtp' : 'gaia'}
                onChange={e => setIsSmtpMode(e.target.value === 'smtp')}
                style={{ fontWeight: 'bold' }}
              >
                <option value="gaia">🛡️ GaiaCOM Quantensicheres Netzwerk (Ed25519 & ML-KEM)</option>
                <option value="smtp">📧 Standard E-Mail Bridge (Klassisch / SMTP unverschlüsselt)</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                {isSmtpMode 
                  ? 'Empfänger (Standard E-Mail Adresse)' 
                  : 'Empfänger (Gaia-Adresse)'
                }
              </label>
              <input
                type="text"
                className="input-field"
                placeholder={isSmtpMode ? "external@gmail.com" : "alice@gaiacom.de"}
                value={composeTo}
                onChange={e => setComposeTo(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Betreff</label>
              <input
                type="text"
                className="input-field"
                placeholder="Projektplanung 2026..."
                value={composeSubject}
                onChange={e => setComposeSubject(e.target.value)}
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <label>Nachricht</label>
              <div className="markdown-toolbar">
                <button type="button" className="toolbar-btn" onClick={() => insertFormat('bold')}>B</button>
                <button type="button" className="toolbar-btn" onClick={() => insertFormat('italic')}>I</button>
                <button type="button" className="toolbar-btn" onClick={() => insertFormat('underline')}>U</button>
                <button type="button" className="toolbar-btn" onClick={() => insertFormat('heading')}>H3</button>
                <button type="button" className="toolbar-btn" onClick={() => insertFormat('code')}>&lt;/&gt;</button>
              </div>
              <textarea
                ref={textareaRef}
                className="input-field"
                placeholder="Schreibe deine Nachricht..."
                style={{ flex: 1, minHeight: '200px', resize: 'none', lineHeight: '1.5', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)' }}
                value={composeBody}
                onChange={e => setComposeBody(e.target.value)}
                required
              />
            </div>

            {/* Attachments Section */}
            <div className="form-group">
              <label>Anhänge</label>
              <div className="attachment-upload-zone" onClick={() => fileInputRef.current.click()}>
                <Icons.Attachment />
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                  {isSmtpMode 
                    ? 'Datei auswählen (Maximal 30 MB für klassische unverschlüsselte Mails).'
                    : 'Klicke hier, um eine Datei auszuwählen und post-quantum E2E verschlüsselt hochzuladen.'
                  }
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </div>

              {uploadFile && (
                <div className="attachment-file-pill">
                  <span>{uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: '0.75rem' }}>{uploadProgress}%</span>
                  {uploadProgress < 100 && (
                    <div className="attachment-progress-bar" style={{ width: '80px', margin: 0 }}>
                      <div className="attachment-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {composeError && <p style={{ color: 'var(--danger)', fontSize: '0.9rem' }}>{composeError}</p>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" style={{ width: 'auto', padding: '12px 24px' }} onClick={() => setIsComposing(false)}>
                Abbrechen
              </button>
              <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 36px' }}>
                <Icons.Sent /> Senden
              </button>
            </div>
          </form>
        )}

        {/* READER MODE */}
        {!isComposing && selectedMail && currentMenu !== 'chat' && currentMenu !== 'profile' && currentMenu !== 'groups' && (
          <>
            <header className="reader-header">
              <button type="button" className="mobile-back-btn" onClick={() => setSelectedMail(null)}>? Mails</button>
              <div className="reader-meta-info">
                <h2>{selectedMail.subject}</h2>
                <div className="sender-address">
                  Von: <button type="button" className="link-button" onClick={() => openContactProfile(selectedMail.sender)}>
                    {selectedMail.senderGaia}
                  </button>
                </div>
                <div className="recipient-address">
                  An: {selectedMail.recipientGaia}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span className="mail-time">{new Date(selectedMail.createdAt).toLocaleString()}</span>
                <button className="btn-action" onClick={() => handleReplyMail(selectedMail)}>
                  Antworten
                </button>
                {selectedMail.sender !== activeIdentity.GaiaID && selectedMail.sender !== activeIdentity.ID && (
                  <button className="btn-action btn-danger" onClick={() => handleReportMail(selectedMail)}>
                    Melden
                  </button>
                )}
              </div>
            </header>

            {/* Content Body */}
            <div className="reader-body">
              {/* Warnings */}
              {selectedMail.untrusted && (
                <div className="untrusted-mail-banner">
                  <Icons.Alert />
                  <div>
                    <strong>SPAM/WARNUNG:</strong> Der Absender dieser Mail befindet sich im Netzwerk-Quarantäne Status wegen Missbrauchs-Meldungen.
                  </div>
                </div>
              )}
              {selectedMail.isSmtp && (
                <div className="smtp-security-banner">
                  <Icons.Alert />
                  <div>
                    Diese Mail wurde über das klassische, unverschlüsselte SMTP-Protokoll übertragen. Keine End-to-End-Verschlüsselung aktiv.
                  </div>
                </div>
              )}

              {/* Text */}
              <p>{renderMarkdown(selectedMail.body)}</p>

              {/* Attachments rendering */}
              {selectedMail.attachments && selectedMail.attachments.length > 0 && (
                <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                  <h4 style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--accent-cyan)', marginBottom: '12px' }}>
                    Anhänge ({selectedMail.attachments.length})
                  </h4>
                  {selectedMail.attachments.map((att, idx) => (
                    <div key={idx} className="attachment-file-pill" style={{ cursor: 'pointer' }} onClick={() => window.open(att.downloadUrl, '_blank')}>
                      <Icons.Attachment />
                      <span>{att.name} ({(att.size / 1024).toFixed(1)} KB)</span>
                      <Icons.Download />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* EMPTY READER STATE */}
        {!isComposing && !selectedMail && currentMenu !== 'chat' && currentMenu !== 'profile' && currentMenu !== 'groups' && (
          <div className="empty-reader-pane">
            <h2>GaiaCOM Reader</h2>
            <p style={{ maxWidth: '380px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Wähle eine Mail aus der Liste aus, um deren quantensichere Inhalte zu lesen, oder erstelle eine neue Nachricht.
            </p>
          </div>
        )}
      </main>

      {/* CUSTOM ALERTS MODALS */}
      {alertConfig && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ borderColor: alertConfig.type === 'danger' ? 'var(--danger)' : alertConfig.type === 'warning' ? 'var(--warning)' : 'var(--accent-cyan)' }}>
            <div className="popup-icon" style={{ 
              color: alertConfig.type === 'danger' ? 'var(--danger)' : alertConfig.type === 'warning' ? 'var(--warning)' : 'var(--accent-cyan)',
              background: alertConfig.type === 'danger' ? 'var(--danger-glow)' : alertConfig.type === 'warning' ? 'var(--warning-glow)' : 'rgba(0, 242, 254, 0.1)'
            }}>
              {alertConfig.type === 'danger' ? '✖' : alertConfig.type === 'warning' ? '⚠️' : '✓'}
            </div>
            <div className="popup-title">{alertConfig.title}</div>
            <div className="popup-text">{alertConfig.text}</div>
            <button className="btn-primary" onClick={() => setAlertConfig(null)}>Fortfahren</button>
          </div>
        </div>
      )}

      {/* MODAL: ADD CONTACT */}
      {showAddContact && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '440px', textAlign: 'left' }}>
            <div className="modal-title">Kontakt hinzufügen</div>
            <form onSubmit={handleDiscoverSubmit}>
              <div className="form-group">
                <label>Suchen per GaiaID / E-Mail</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="bob@gaiacom.de"
                    value={discoverGaiaId}
                    onChange={e => setDiscoverGaiaId(e.target.value)}
                  />
                  <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0 18px' }}>
                    Suchen
                  </button>
                </div>
              </div>
            </form>

            {discoverError && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: '10px' }}>{discoverError}</p>}

            {discoveredContact && (
              <div className="mail-card" style={{ background: 'rgba(255,255,255,0.02)', margin: '15px 0' }}>
                <div className="mail-card-header">
                  <div className="mail-sender">{discoveredContact.displayName}</div>
                  <button className="btn-action" onClick={addDiscoveredContact}>
                    + Hinzufügen
                  </button>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  {displayGaiaID(discoveredContact.gaiaID)}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAddContact(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {contactProfile && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '520px', textAlign: 'left' }}>
            <div className="modal-title">{contactProfile.displayName || 'GaiaCOM Kontakt'}</div>
            <div className="contact-profile-grid">
              <div>
                <label>GaiaID</label>
                <code>{displayGaiaID(contactProfile.gaiaID)}</code>
              </div>
              <div>
                <label>Identity Fingerprint</label>
                <code>{contactProfile.publicKey ? `${contactProfile.publicKey.slice(0, 24)}...${contactProfile.publicKey.slice(-24)}` : 'Nicht verfügbar'}</code>
              </div>
              <div>
                <label>TrustMesh</label>
                <span>Score {contactProfile.abuseScore?.score || 0}, Level {contactProfile.abuseScore?.escalationLevel || 0}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setIsComposing(true);
                  setComposeTo(displayGaiaID(contactProfile.gaiaID));
                  setComposeSubject('');
                  setComposeBody('');
                  setComposeReplyTo(null);
                  setSelectedMail(null);
                  setContactProfile(null);
                }}
              >
                Mail schreiben
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setActiveChatContact(contactProfile);
                  setCurrentMenu('chat');
                  setContactProfile(null);
                }}
              >
                Chat öffnen
              </button>
              <button type="button" className="btn-primary" style={{ width: 'auto', padding: '0 20px' }} onClick={() => setContactProfile(null)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: QUANTUM SHIELD EXPLANATION */}
      {showQuantumShieldModal && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '600px', textAlign: 'left' }}>
            <div className="modal-title" style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--accent-cyan)', marginBottom: '15px' }}>
              🛡️ {t('quantum_shield_status').trim()}
            </div>
            <div style={{ fontSize: '0.85rem', lineHeight: '1.6', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: '12px' }}>
                {t('quantum_shield_desc')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '15px 0' }}>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '4px', borderLeft: '3px solid var(--accent-cyan)' }}>
                  <strong>{t('quantum_shield_l1_title')}</strong>
                  <p style={{ fontSize: '0.8rem', marginTop: '2px', color: 'var(--text-muted)' }}>
                    {t('quantum_shield_l1_desc')}
                  </p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '4px', borderLeft: '3px solid var(--accent-violet)' }}>
                  <strong>{t('quantum_shield_l2_title')}</strong>
                  <p style={{ fontSize: '0.8rem', marginTop: '2px', color: 'var(--text-muted)' }}>
                    {t('quantum_shield_l2_desc')}
                  </p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '4px', borderLeft: '3px solid var(--success)' }}>
                  <strong>{t('quantum_shield_l3_title')}</strong>
                  <p style={{ fontSize: '0.8rem', marginTop: '2px', color: 'var(--text-muted)' }}>
                    {t('quantum_shield_l3_desc')}
                  </p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '4px', borderLeft: '3px solid var(--warning)' }}>
                  <strong>{t('quantum_shield_l4_title')}</strong>
                  <p style={{ fontSize: '0.8rem', marginTop: '2px', color: 'var(--text-muted)' }}>
                    {t('quantum_shield_l4_desc')}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="btn-primary" style={{ width: 'auto', padding: '10px 24px' }} onClick={() => setShowQuantumShieldModal(false)}>
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CREATE GROUP */}
      {showCreateGroupModal && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '440px', textAlign: 'left' }}>
            <div className="modal-title">Neue Gruppe erstellen</div>
            <form onSubmit={handleCreateRoom}>
              <div className="form-group">
                <label>Gruppenname</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="Projekt Alpha..." 
                  value={groupNameInput}
                  onChange={e => setGroupNameInput(e.target.value)}
                  required 
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="Quantensicherer Austausch über..." 
                  value={groupDescriptionInput}
                  onChange={e => setGroupDescriptionInput(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Gruppen-Icon / Avatar</label>
                <div className="avatar-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
                  {['👥', '🔒', '🛡️', '⚡', '🚀', '🧠', '💻', '🌌', '🧪', '🔥', '✨', 'ðŸŒ'].map(emoji => (
                    <div 
                      key={emoji} 
                      className={`avatar-item ${groupAvatarInput === emoji ? 'active' : ''}`}
                      onClick={() => setGroupAvatarInput(emoji)}
                      style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}
                    >
                      {emoji}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowCreateGroupModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0 20px' }}>
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: JOIN GROUP */}
      {showJoinGroupModal && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '440px', textAlign: 'left' }}>
            <div className="modal-title">Gruppe beitreten</div>
            <form onSubmit={handleJoinRoom}>
              <div className="form-group">
                <label>Einladungscode (Secret Hash)</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="Code (64-stelliger Hex-Hash)..." 
                  value={joinGroupHashInput}
                  onChange={e => setJoinGroupHashInput(e.target.value)}
                  required 
                />
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowJoinGroupModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0 20px' }}>
                  Beitreten
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: CREATE CHANNEL */}
      {showCreateChannelModal && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '440px', textAlign: 'left' }}>
            <div className="modal-title">Neuen Kanal erstellen</div>
            <form onSubmit={handleCreateChannel}>
              <div className="form-group">
                <label>Kanalname</label>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="z.B. marketing" 
                  value={newChannelNameInput}
                  onChange={e => setNewChannelNameInput(e.target.value)}
                  required 
                />
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowCreateChannelModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0 20px' }}>
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGroupSettingsModal && (
        <GroupSettingsModal
          name={editGroupName}
          description={editGroupDescription}
          avatar={editGroupAvatar}
          onNameChange={setEditGroupName}
          onDescriptionChange={setEditGroupDescription}
          onAvatarChange={setEditGroupAvatar}
          onSubmit={handleUpdateGroupSettings}
          onClose={() => setShowGroupSettingsModal(false)}
          onDelete={handleDeleteGroup}
        />
      )}

      {/* KEY CHANGE WARNING MODAL (PLATIN UX) */}
      {keyChangeWarning && (
        <div className="popup-overlay">
          <div className="popup-card glass-panel" style={{ width: '100%', maxWidth: '520px', borderColor: 'var(--danger)', textAlign: 'left' }}>
            <div className="popup-icon" style={{ color: 'var(--danger)', background: 'var(--danger-glow)', margin: '0 0 20px 0' }}>⚠️</div>
            <div className="popup-title" style={{ color: 'var(--danger)', fontSize: '1.3rem' }}>Sicherheitskritischer Schlüsselwechsel!</div>
            
            <div style={{ fontSize: '0.85rem', lineHeight: '1.6', color: 'var(--text-primary)', marginBottom: '15px' }}>
              <p style={{ marginBottom: '10px' }}>
                Der Identitätsschlüssel für den Kontakt <strong>{keyChangeWarning.displayName}</strong> ({displayGaiaID(keyChangeWarning.gaiaID)}) hat sich geändert. Dies kann ein Anzeichen für einen Man-in-the-Middle-Angriff sein!
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '4px', border: '1px solid var(--border-color)', fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                <div>
                  <strong style={{ color: 'var(--text-muted)' }}>ALTER FINGERPRINT:</strong><br/>
                  <span style={{ color: 'var(--text-secondary)' }}>{keyChangeWarning.oldKey.slice(0, 16)}...{keyChangeWarning.oldKey.slice(-16)}</span>
                </div>
                <div>
                  <strong style={{ color: 'var(--accent-cyan)' }}>NEUER FINGERPRINT:</strong><br/>
                  <span style={{ color: 'var(--accent-cyan)' }}>{keyChangeWarning.newKey.slice(0, 16)}...{keyChangeWarning.newKey.slice(-16)}</span>
                </div>
              </div>
              
              <p style={{ marginTop: '12px', fontWeight: 600, color: 'var(--warning)' }}>
                Verifiziere den neuen Fingerprint über einen alternativen sicheren Kanal.
              </p>
            </div>
            
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ color: 'var(--danger)' }}>
                Gib die letzten 6 Zeichen des neuen Schlüssels zur Bestätigung ein ({keyChangeWarning.newKey.slice(-6)}):
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="z.B. a1b2c3"
                value={keyChangeConfirmInput}
                onChange={e => setKeyChangeConfirmInput(e.target.value)}
                style={{ borderColor: 'var(--danger)' }}
                maxLength={6}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn-secondary" 
                onClick={keyChangeWarning.cancelFn}
                style={{ flex: 1, marginTop: 0 }}
              >
                Abbrechen
              </button>
              <button 
                className="btn-primary" 
                onClick={keyChangeWarning.resumeFn}
                disabled={keyChangeConfirmInput.toLowerCase() !== keyChangeWarning.newKey.slice(-6).toLowerCase()}
                style={{ flex: 1, background: 'var(--danger)', boxShadow: '0 4px 20px var(--danger-glow)', marginTop: 0 }}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
